import React, { useEffect, useState } from "react";
import api, { CLP } from "@/lib/api";
import { ShieldCheck, Wrench, TrendingUp, TrendingDown, Minus } from "lucide-react";

function daysLeft(iso) {
  if (!iso) return null;
  const today = new Date();
  today.setHours(0, 0, 0, 0);
  const target = new Date(iso);
  return Math.ceil((target - today) / (1000 * 60 * 60 * 24));
}

function MiniSpark({ points }) {
  if (!points || points.length < 2) return null;
  const w = 100;
  const h = 24;
  const prices = points.map((p) => p.price || 0);
  const min = Math.min(...prices);
  const max = Math.max(...prices);
  const span = max - min || 1;
  const step = w / (points.length - 1);
  const d = points
    .map((p, i) => `${i === 0 ? "M" : "L"} ${(i * step).toFixed(1)} ${(h - ((p.price - min) / span) * h).toFixed(1)}`)
    .join(" ");
  return (
    <svg width={w} height={h} className="text-[#FF5A00]">
      <path d={d} fill="none" stroke="currentColor" strokeWidth="1.5" />
    </svg>
  );
}

export default function CustomerRepairHistory({ customerId }) {
  const [data, setData] = useState({ history: [], evolution: [] });
  const [warranties, setWarranties] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    let mounted = true;
    (async () => {
      setLoading(true);
      try {
        const [h, w] = await Promise.all([
          api.get(`/customers/${customerId}/repair-history`),
          api.get(`/customers/${customerId}/active-warranties`),
        ]);
        if (mounted) {
          setData(h.data);
          setWarranties(w.data);
        }
      } finally {
        if (mounted) setLoading(false);
      }
    })();
    return () => { mounted = false; };
  }, [customerId]);

  if (loading) return null;

  const totalRepairs = data.history.length;
  const totalSpent = data.history.reduce((acc, r) => acc + (r.quantity || 1) * (r.unit_price || 0), 0);

  return (
    <section className="bg-white border border-neutral-200" data-testid="customer-repair-history">
      <div className="px-5 py-3 border-b border-neutral-200 flex items-center justify-between">
        <div className="flex items-center gap-2 font-display font-bold">
          <Wrench className="w-4 h-4 text-[#FF5A00]" />
          Historial de reparaciones ({totalRepairs})
        </div>
        {totalSpent > 0 && (
          <div className="font-mono-kpi text-xs">
            Total: <span className="font-bold">{CLP(totalSpent)}</span>
          </div>
        )}
      </div>

      {/* Active warranties banner */}
      {warranties.length > 0 && (
        <div className="px-5 py-3 bg-[#00C853]/5 border-b border-[#00C853]/20" data-testid="active-warranties-banner">
          <div className="flex items-center gap-2 text-[#008C3A] font-mono-kpi text-[10px] uppercase tracking-[0.18em] mb-2">
            <ShieldCheck className="w-3.5 h-3.5" />
            Garantías activas ({warranties.length})
          </div>
          <div className="space-y-1.5">
            {warranties.map((w) => {
              const dl = daysLeft(w.warranty_until);
              return (
                <div key={w.service_id} className="flex items-center justify-between text-xs">
                  <div className="text-neutral-700 truncate">
                    Servicio {w.scheduled_date} · {(w.repair_items || []).length} reparación{(w.repair_items || []).length !== 1 ? "es" : ""}
                  </div>
                  <span
                    data-testid={`warranty-badge-${w.service_id}`}
                    className="inline-flex items-center gap-1 px-2 py-0.5 bg-[#00C853]/10 text-[#008C3A] border border-[#00C853]/40 font-mono-kpi text-[9px] uppercase tracking-wider"
                  >
                    <ShieldCheck className="w-2.5 h-2.5" />
                    Vence en {dl} días
                  </span>
                </div>
              );
            })}
          </div>
        </div>
      )}

      {totalRepairs === 0 ? (
        <div className="px-5 py-6 text-sm text-neutral-500">Sin reparaciones registradas para este cliente.</div>
      ) : (
        <>
          {/* Evolution per description */}
          {data.evolution.length > 0 && (
            <div className="px-5 py-3 border-b border-neutral-100">
              <div className="font-mono-kpi text-[10px] uppercase tracking-[0.2em] text-neutral-500 mb-2">
                Evolución de precios
              </div>
              <div className="space-y-2">
                {data.evolution.slice(0, 5).map((e, i) => {
                  const trend = e.last_price - e.first_price;
                  const TrendIcon = trend > 0 ? TrendingUp : trend < 0 ? TrendingDown : Minus;
                  const trendCls = trend > 0 ? "text-[#D50000]" : trend < 0 ? "text-[#00C853]" : "text-neutral-400";
                  return (
                    <div key={`ev-${e.description}-${i}`} className="flex items-center justify-between gap-3 text-xs py-1">
                      <div className="min-w-0 flex-1">
                        <div className="truncate" title={e.description}>{e.description}</div>
                        <div className="text-[10px] text-neutral-400 font-mono-kpi">{e.occurrences}× · {e.first_date} → {e.last_date}</div>
                      </div>
                      <MiniSpark points={e.points} />
                      <div className={`flex items-center gap-1 font-mono-kpi font-bold ${trendCls}`}>
                        <TrendIcon className="w-3 h-3" />
                        {CLP(e.last_price)}
                      </div>
                    </div>
                  );
                })}
              </div>
            </div>
          )}

          {/* Detailed list */}
          <div className="max-h-80 overflow-y-auto divide-y divide-neutral-100">
            {data.history.slice().reverse().map((r, i) => {
              const dl = daysLeft(r.warranty_until);
              const isActive = dl != null && dl >= 0;
              return (
                <div key={`h-${r.service_date}-${r.description}-${i}`} className="px-5 py-2.5 flex items-center justify-between gap-3 text-xs hover:bg-neutral-50">
                  <div className="min-w-0 flex-1">
                    <div className="truncate font-medium">{r.description}</div>
                    <div className="text-[10px] text-neutral-500 font-mono-kpi">
                      {r.service_date} · cant: {r.quantity}
                    </div>
                  </div>
                  <div className="font-mono-kpi font-bold whitespace-nowrap">{CLP(r.unit_price)}</div>
                  {isActive && (
                    <span className="inline-flex items-center gap-1 px-1.5 py-0.5 bg-[#00C853]/10 text-[#008C3A] border border-[#00C853]/40 font-mono-kpi text-[8px] uppercase tracking-wider">
                      <ShieldCheck className="w-2.5 h-2.5" />
                      Garantía
                    </span>
                  )}
                </div>
              );
            })}
          </div>
        </>
      )}
    </section>
  );
}
